import { useState, useEffect, useRef } from "react";
import {
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Snackbar,
  Alert,
} from "@mui/material";

import axiosClient from "../axiosClient";
import { useTranslation } from "react-i18next";
import CalendarCheckbox from "../components/SmallCalendar";

const AppointmentForm = () => {
  const [error, setError] = useState("");
  const [staffUsers, setStaffUsers] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [selectedClient, setSelectedClient] = useState("");
  const [selectedStaff, setSelectedStaff] = useState("");
  const [isSendEmail, setIsSendEmail] = useState(false);
  const [isSendSMS, setIsSendSMS] = useState(false);
  const [smsLanguage, setSmsLanguage] = useState("en"); // Language for SMS (en or el)
  const [earlierPatientsList, setEarlierPatientsList] = useState([]);
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedDateCalendar, setSelectedDateCalendar] = useState(null);
  // eslint-disable-next-line
  const [earlierDate, setEarlierDate] = useState(""); // New state for earlier date
  const [loading, setLoading] = useState(false);
  const [snackbarState, setSnackbarState] = useState({
    appointment: { open: false, message: "" },
    email: { open: false, message: "" },
    sms: { open: false, message: "" },
  });

  const { t } = useTranslation();

  const dateRef = useRef(null);
  const clientRef = useRef(null);
  const timestartRef = useRef(null);
  const timeendRef = useRef(null);
  const staffRef = useRef(null);
  const earlierDateRef = useRef(null); // New ref for earlier date

  useEffect(() => {
    if (selectedDate && selectedStaff) {
      getEarlierPatients(selectedDate, selectedStaff);
    }
  }, [selectedDate, selectedStaff]);

  const getEarlierPatients = (date, staff) => {
    setLoading(true);
    axiosClient
      .get(`api/print_appointments/?earlier_date=${date}&staff=${staff}`)
      .then(({ data }) => {
        console.log("Earlier Patients Data:", data);
        if (Array.isArray(data)) {
          setEarlierPatientsList(data);
        } else {
          setError("Invalid patient data received.");
        }
      })
      .catch((err) => {
        setError("Failed to load earlier patients.");
      })
      .finally(() => {
        setLoading(false);
      });
  };

  // const formatDate = (date) => {
  //   if (!date) return "";
  //   const year = date.getFullYear();
  //   const month = String(date.getMonth() + 1).padStart(2, "0");
  //   const day = String(date.getDate()).padStart(2, "0");
  //   return `${year}-${month}-${day}`;
  // };

  const clearForm = () => {
    setSelectedClient("");
    setSelectedStaff("");
    setSelectedDate("");
    setSelectedDateCalendar(null);
    setEarlierDate(""); // Reset earlier date
    setIsSendEmail(false);
    setIsSendSMS(false);
    setEarlierPatientsList([]);
    setError("");
    if (dateRef.current) dateRef.current.value = "";
    if (timestartRef.current) timestartRef.current.value = "";
    if (timeendRef.current) timeendRef.current.value = "";
    if (earlierDateRef.current) earlierDateRef.current.value = ""; // Clear earlier date field
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSnackbarState({
      appointment: { open: false, message: "" },
      email: { open: false, message: "" },
      sms: { open: false, message: "" },
    });

    const payload = {
      client_id: selectedClient,
      staff_id: selectedStaff,
      appointment_start_time_str: timestartRef.current.value,
      appointment_finish_time_str: timeendRef.current.value,
      appointment_date: dateRef.current.value,
      earlier_date: earlierDateRef.current.value || "", // Include earlier date
    };

    if (
      !selectedClient ||
      !selectedStaff ||
      !payload.appointment_start_time_str ||
      !payload.appointment_finish_time_str ||
      !payload.appointment_date
    ) {
      setError("Please fill out all required fields.");
      return;
    }

    // Validate that earlier_date is before appointment_date if set
    if (payload.earlier_date && payload.appointment_date) {
      const earlierDateObj = new Date(payload.earlier_date);
      const apptDate = new Date(payload.appointment_date);
      if (earlierDateObj >= apptDate) {
        setError("Earlier date must be before the appointment date.");
        return;
      }
    }

    try {
      const res = await axiosClient.post("api/appointment/", payload);
      if (res?.status === 201) {
        setSnackbarState((prev) => ({
          ...prev,
          appointment: { open: true, message: t("Appointment added successfully!") },
        }));
        if (isSendEmail) {
          await handleSendEmail(res?.data?.id);
        }
        if (isSendSMS) {
          await handleSendSMS(res?.data?.id);
        }
        clearForm();
      }
    } catch (err) {
      setError(err?.response?.data?.error || t("An unexpected error occurred."));
    }
  };

  const handleSendEmail = async (id) => {
    if (!id) {
      setError(t("Cannot send email: Invalid appointment ID."));
      return;
    }
    try {
      await axiosClient.post("api/send_email/", { appointment_id: id });
      setSnackbarState((prev) => ({
        ...prev,
        email: { open: true, message: t("Email sent successfully!") },
      }));
    } catch (error) {
      setError(t("Failed to send email. Please try again."));
      console.error(error);
    }
  };

  const handleSendSMS = async (id) => {
    if (!id) {
      setError(t("Cannot send SMS: Invalid appointment ID."));
      return;
    }
    try {
      await axiosClient.post(`api/send_appointment_info/${id}/`, {
        language: smsLanguage,
      });
      setSnackbarState((prev) => ({
        ...prev,
        sms: { open: true, message: t("SMS sent successfully!") },
      }));
    } catch (error) {
      setError(t("Failed to send SMS. Please try again."));
      console.error(error);
    }
  };

  const handleCloseSnackbar = (type) => {
    setSnackbarState((prev) => ({
      ...prev,
      [type]: { ...prev[type], open: false },
    }));
  };

  useEffect(() => {
    Promise.all([
      axiosClient.get("api/staff-users/"),
      axiosClient.get("api/client-users/"),
    ]).then(([staffRes, clientRes]) => {
      setStaffUsers(staffRes?.data);
      setClientUsers(clientRes?.data);
    });
  }, []);

  const handleDateChange = (e) => {
    setSelectedDate(e.target.value);
  };

  const handleEarlierDateChange = (e) => {
    setEarlierDate(e.target.value);
  };

  useEffect(() => {
    if (selectedStaff) {
      const filteredData = clientUsers.filter(
        (user) => user.assigned_staff === selectedStaff
      );
      setClientUsers(filteredData);
    }
    // eslint-disable-next-line
  }, [selectedStaff]);

  return (
    <Card elevation={16}>
      <CardContent>
        <Typography variant="h5" gutterBottom sx={{ fontSize: "1.2rem" }}>
          {t("Appointment Details")}
        </Typography>
        {error && (
          <Alert severity="error" sx={{ marginBottom: "1rem" }}>
            {error}
          </Alert>
        )}
        <form onSubmit={handleSubmit}>
          <Box sx={{ "& .MuiTextField-root": { marginBottom: "1rem" } }}>
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ marginBottom: "1rem" }}
              required
            >
              <InputLabel id="staff-select-label">
                {t("Select Staff")}
              </InputLabel>
              <Select
                labelId="staff-select-label"
                inputRef={staffRef}
                value={selectedStaff}
                onChange={(e) => setSelectedStaff(e.target.value)}
                label={t("Select Staff")}
              >
                {staffUsers.map((st) => (
                  <MenuItem
                    value={st.id}
                    lastname={st.custom_user.last_name}
                  >{`${st.custom_user.first_name} ${st.custom_user.last_name}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              fullWidth
              variant="outlined"
              sx={{ marginBottom: "1rem" }}
              required
            >
              <InputLabel id="client-select-label">
                {t("Select Client")}
              </InputLabel>
              <Select
                labelId="client-select-label"
                inputRef={clientRef}
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                label={t("Select Client")}
              >
                {clientUsers.map((user) => (
                  <MenuItem
                    value={user.id}
                    lastname={user.custom_user.last_name}
                  >{`${user.custom_user.first_name} ${user.custom_user.last_name}`}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              type="date"
              label={t("Date of Appointment")}
              variant="outlined"
              onChange={handleDateChange}
              InputLabelProps={{ shrink: true }}
              inputRef={dateRef}
              required
            />
            <TextField
              fullWidth
              type="time"
              label={t("Time of Appointment (start)")}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              inputRef={timestartRef}
              required
            />
            <TextField
              fullWidth
              type="time"
              label={t("Time of Appointment (end)")}
              variant="outlined"
              InputLabelProps={{ shrink: true }}
              inputRef={timeendRef}
              required
            />
          </Box>
          <TextField
            fullWidth
            type="date"
            label={t("Earlier Appointment Date (If any)")}
            variant="outlined"
            onChange={handleEarlierDateChange}
            InputLabelProps={{ shrink: true }}
            inputRef={earlierDateRef}
          />
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              gap: 2,
              marginTop: 4,
            }}
          >
            <FormGroup>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSendEmail}
                    onChange={(e) => setIsSendEmail(e.target.checked)}
                  />
                }
                label={t("Send Email to client")}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={isSendSMS}
                    onChange={(e) => setIsSendSMS(e.target.checked)}
                  />
                }
                label={t("Send SMS to client")}
              />
              {isSendSMS && (
                <FormControl size="small" sx={{ minWidth: 120, ml: 4, mb: 1 }}>
                  <InputLabel>{t("SMS Language")}</InputLabel>
                  <Select
                    value={smsLanguage}
                    label={t("SMS Language")}
                    onChange={(e) => setSmsLanguage(e.target.value)}
                  >
                    <MenuItem value="en">{t("English")}</MenuItem>
                    <MenuItem value="el">{t("Greek")}</MenuItem>
                  </Select>
                </FormControl>
              )}
              <CalendarCheckbox
                selectedDateCalendar={selectedDateCalendar}
                setSelectedDateCalendar={setSelectedDateCalendar}
                selectedClient={selectedClient}
              />
            </FormGroup>
            <Box
              sx={{
                flex: { xs: 1, sm: 0.4 },
                maxHeight: "300px",
                overflowY: "auto",
                bgcolor: "background.paper",
                borderRadius: 2,
                boxShadow: 2,
                p: { xs: 1.5, sm: 2 },
                border: "1px solid",
                borderColor: "grey.300",
                mt: { xs: 2, sm: 0 },
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontSize: { xs: ".9rem", sm: "1.1rem" },
                  fontWeight: "medium",
                  mb: 1.5,
                  color: "text.primary",
                }}
              >
                {t("Earlier Appointments for Selected Patient and Doctor")}
              </Typography>
              {loading ? (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", textAlign: "center" }}
                >
                  {t("Loading...")}
                </Typography>
              ) : earlierPatientsList.length === 0 ? (
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", textAlign: "center" }}
                >
                  {t("No earlier appointments found.")}
                </Typography>
              ) : (
                <List dense sx={{ p: 0 }}>
                  {earlierPatientsList.map((patient) => (
                    <ListItem
                      key={patient.id}
                      sx={{
                        py: 0.5,
                        "&:not(:last-child)": { borderBottom: "1px solid", borderColor: "grey.200" },
                      }}
                    >
                      <ListItemText
                        primary={
                          <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", textAlign: "center" }}
                          >
                            {t("Yes, {{clientName}} has an earlier appointment.", {
                              clientName: `${patient.client_name} ${patient.client_surname}`,
                            })}
                          </Typography>
                        }
                      />
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          </Box>
          <Box sx={{ display: "flex", gap: "1rem", marginTop: "1rem" }}>
            <Button type="submit" variant="contained" color="primary">
              {t("Submit")}
            </Button>
            <Button
              variant="outlined"
              color="secondary"
              onClick={clearForm}
            >
              {t("Clear")}
            </Button>
          </Box>
        </form>
        <Snackbar
          open={snackbarState.appointment.open}
          autoHideDuration={6000}
          onClose={() => handleCloseSnackbar("appointment")}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => handleCloseSnackbar("appointment")}
            severity="success"
            sx={{ width: "100%" }}
          >
            {snackbarState.appointment.message}
          </Alert>
        </Snackbar>
        <Snackbar
          open={snackbarState.email.open}
          autoHideDuration={6000}
          onClose={() => handleCloseSnackbar("email")}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => handleCloseSnackbar("email")}
            severity="success"
            sx={{ width: "100%" }}
          >
            {snackbarState.email.message}
          </Alert>
        </Snackbar>
        <Snackbar
          open={snackbarState.sms.open}
          autoHideDuration={6000}
          onClose={() => handleCloseSnackbar("sms")}
          anchorOrigin={{ vertical: "top", horizontal: "right" }}
        >
          <Alert
            onClose={() => handleCloseSnackbar("sms")}
            severity="success"
            sx={{ width: "100%" }}
          >
            {snackbarState.sms.message}
          </Alert>
        </Snackbar>
      </CardContent>
    </Card>
  );
};

export default AppointmentForm;